import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface Account {
  id: string;
  name: string;
}

interface YearWorkbook {
  id: string;
  year: number;
  spreadsheetId: string;
  spreadsheetUrl: string | null;
  createdAt: string;
}

interface ExportInfo {
  id: string;
  name: string;
  dataSource: string;
  schedule: string;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncRows: number;
}

interface SyncEntry {
  id: string;
  exportName: string;
  dataSource: string;
  status: string;
  rowsSynced: number;
  durationMs: number | null;
  startedAt: string;
  completedAt: string | null;
}

interface OverviewData {
  workbooks: YearWorkbook[];
  exports: ExportInfo[];
  recentSync: SyncEntry[];
  ppcWorkbookSpreadsheetId: string | null;
  todayStats: { totalSyncs: number; succeeded: number; totalRows: number };
  tabCount: number;
}

// ── Component ────────────────────────────────────────────────────────────────

export function SheetsOverview() {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setError('');
        const accounts = await api.get<Account[]>('/accounts');
        if (accounts.length === 0) {
          setData(null);
          setLoading(false);
          return;
        }

        // Fetch overview for all accounts in parallel
        const results = await Promise.all(
          accounts.map((a) => api.get<OverviewData>(`/sheets/overview/${a.id}`)),
        );

        // Merge results
        const merged: OverviewData = {
          workbooks: [],
          exports: [],
          recentSync: [],
          ppcWorkbookSpreadsheetId: null,
          todayStats: { totalSyncs: 0, succeeded: 0, totalRows: 0 },
          tabCount: 0,
        };

        for (const r of results) {
          merged.workbooks.push(...r.workbooks);
          merged.exports.push(...r.exports);
          merged.recentSync.push(...r.recentSync);
          if (!merged.ppcWorkbookSpreadsheetId && r.ppcWorkbookSpreadsheetId) {
            merged.ppcWorkbookSpreadsheetId = r.ppcWorkbookSpreadsheetId;
          }
          merged.todayStats.totalSyncs += r.todayStats.totalSyncs;
          merged.todayStats.succeeded += r.todayStats.succeeded;
          merged.todayStats.totalRows += r.todayStats.totalRows;
          if (r.tabCount > merged.tabCount) merged.tabCount = r.tabCount;
        }

        // Sort merged recent sync by startedAt descending, keep top 20
        merged.recentSync.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
        merged.recentSync = merged.recentSync.slice(0, 20);

        // Deduplicate workbooks by year (same year across accounts)
        const seenYears = new Set<number>();
        merged.workbooks = merged.workbooks.filter((wb) => {
          if (seenYears.has(wb.year)) return false;
          seenYears.add(wb.year);
          return true;
        });

        setData(merged);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <LoadingSkeleton />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-base font-semibold">Overview</h2>
        <p className="text-sm text-gray-500">
          Year workbooks, exports, and sync activity — all in one place.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {data && (
        <>
          {/* KPI Cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Year Workbook Tabs" value={String(data.tabCount)} />
            <StatCard label="Active Exports" value={String(data.exports.length)} />
            <StatCard label="Syncs Today" value={String(data.todayStats.totalSyncs)} sub={`${data.todayStats.succeeded} succeeded`} />
            <StatCard label="Rows Today" value={fmtNumber(data.todayStats.totalRows)} />
          </div>

          {/* Year Workbooks */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
            <h3 className="text-sm font-semibold">Year Workbooks</h3>
            {data.workbooks.length === 0 ? (
              <p className="text-sm text-gray-500">
                No year workbooks yet.{' '}
                <Link to="/sheets?tab=connections" className="text-brand-600 hover:underline">
                  Create one from Connections
                </Link>.
              </p>
            ) : (
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {data.workbooks.map((wb) => (
                  <div key={wb.id} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">Sellvana {wb.year}</p>
                      <p className="text-xs text-gray-500">
                        {data.tabCount} tabs &middot; Created {new Date(wb.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <a
                      href={wb.spreadsheetUrl ?? `https://docs.google.com/spreadsheets/d/${wb.spreadsheetId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-brand-600 hover:bg-gray-50"
                    >
                      Open in Sheets
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* PPC Workbook */}
          {data.ppcWorkbookSpreadsheetId && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">PPC Workbook</h3>
                  <p className="text-xs text-gray-500">Bid suggestions, keyword analysis, and campaign actions</p>
                </div>
                <a
                  href={`https://docs.google.com/spreadsheets/d/${data.ppcWorkbookSpreadsheetId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-brand-600 hover:bg-gray-50"
                >
                  Open in Sheets
                </a>
              </div>
            </div>
          )}

          {/* Active Exports */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Active Exports</h3>
              <Link
                to="/sheets?tab=exports"
                className="text-xs font-medium text-brand-600 hover:text-brand-700"
              >
                Manage Exports
              </Link>
            </div>
            {data.exports.length === 0 ? (
              <p className="text-sm text-gray-500">No active exports configured.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="pb-2 text-left text-xs font-medium text-gray-500">Name</th>
                      <th className="pb-2 text-left text-xs font-medium text-gray-500">Data Source</th>
                      <th className="pb-2 text-center text-xs font-medium text-gray-500">Schedule</th>
                      <th className="pb-2 text-center text-xs font-medium text-gray-500">Status</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Last Sync</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Rows</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.exports.map((exp) => (
                      <tr key={exp.id}>
                        <td className="py-2 font-medium text-gray-900">{exp.name}</td>
                        <td className="py-2 text-gray-600">{formatDataSource(exp.dataSource)}</td>
                        <td className="py-2 text-center">
                          <ScheduleBadge schedule={exp.schedule} />
                        </td>
                        <td className="py-2 text-center">
                          <StatusBadge status={exp.lastSyncStatus} />
                        </td>
                        <td className="py-2 text-right text-gray-500 text-xs">
                          {exp.lastSyncAt ? timeAgo(exp.lastSyncAt) : 'Never'}
                        </td>
                        <td className="py-2 text-right text-gray-700">{fmtNumber(exp.lastSyncRows)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent Sync Timeline */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
            <h3 className="text-sm font-semibold">Recent Sync Activity</h3>
            {data.recentSync.length === 0 ? (
              <p className="text-sm text-gray-500">No sync activity yet.</p>
            ) : (
              <div className="space-y-2">
                {data.recentSync.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between rounded-lg border border-gray-100 px-4 py-2"
                  >
                    <div className="flex items-center gap-3">
                      <StatusDot status={entry.status} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{entry.exportName}</p>
                        <p className="text-xs text-gray-500">{formatDataSource(entry.dataSource)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-700">
                        {fmtNumber(entry.rowsSynced)} rows
                        {entry.durationMs ? ` in ${(entry.durationMs / 1000).toFixed(1)}s` : ''}
                      </p>
                      <p className="text-xs text-gray-400">{timeAgo(entry.startedAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  const styles: Record<string, string> = {
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    running: 'bg-blue-100 text-blue-700',
    pending: 'bg-gray-100 text-gray-500',
  };
  const label = status ?? 'never';
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${styles[label] ?? 'bg-gray-100 text-gray-500'}`}>
      {label}
    </span>
  );
}

function ScheduleBadge({ schedule }: { schedule: string }) {
  const styles: Record<string, string> = {
    hourly: 'bg-purple-100 text-purple-700',
    every_6h: 'bg-blue-100 text-blue-700',
    daily: 'bg-green-100 text-green-700',
    weekly: 'bg-amber-100 text-amber-700',
    manual: 'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${styles[schedule] ?? 'bg-gray-100 text-gray-500'}`}>
      {schedule.replace('_', ' ')}
    </span>
  );
}

function StatusDot({ status }: { status: string }) {
  const colors: Record<string, string> = {
    completed: 'bg-green-400',
    failed: 'bg-red-400',
    running: 'bg-blue-400 animate-pulse',
    pending: 'bg-gray-300',
  };
  return <span className={`inline-block h-2 w-2 rounded-full ${colors[status] ?? 'bg-gray-300'}`} />;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div>
        <div className="h-5 w-32 rounded bg-gray-200" />
        <div className="mt-2 h-4 w-64 rounded bg-gray-200" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-20 rounded-xl bg-gray-200" />
        ))}
      </div>
      <div className="h-40 rounded-xl bg-gray-200" />
      <div className="h-48 rounded-xl bg-gray-200" />
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtNumber(n: number): string {
  return n.toLocaleString();
}

function formatDataSource(ds: string): string {
  return ds
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
